/*
 * Purpose: Gmail inbound connector — create inbox, poll events, test POST
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { getApiBase, gmailWebhookLatestUrl, gmailWebhookPostUrl } from '@/lib/apiBase';
import { resolveSecret } from '@/lib/resolveSecret';

export interface GmailInbox {
  inboxId: string;
  postUrl: string;
  latestUrl: string;
}

export interface GmailEvent {
  from?: string;
  subject?: string;
  body?: string;
  roles?: Array<Record<string, unknown>>;
  receivedAt?: string;
  source?: string;
  [key: string]: unknown;
}

export async function checkWebhookServerHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${getApiBase()}/health`);
    return res.ok;
  } catch {
    return false;
  }
}

export async function createGmailInbox(): Promise<GmailInbox> {
  const res = await fetch(`${getApiBase()}/api/inboxes`, { method: 'POST' });
  if (!res.ok) throw new Error('Could not create Gmail inbox — is the webhook server running?');
  const data = (await res.json()) as GmailInbox;
  return {
    inboxId: data.inboxId,
    postUrl: gmailWebhookPostUrl(data.inboxId),
    latestUrl: gmailWebhookLatestUrl(data.inboxId),
  };
}

export async function fetchLatestGmailEvent(inboxId: string): Promise<GmailEvent | null> {
  const secret = resolveSecret('GMAIL_WEBHOOK_SECRET');
  const headers: Record<string, string> = {};
  if (secret) headers['X-Webhook-Secret'] = secret;

  const res = await fetch(gmailWebhookLatestUrl(inboxId), { headers });
  if (!res.ok) throw new Error(`Gmail poll failed: HTTP ${res.status}`);
  const data = (await res.json()) as { event: GmailEvent | null };
  return data.event;
}

export async function sendTestGmailEvent(
  inboxId: string,
  payload: GmailEvent
): Promise<GmailEvent> {
  const secret = resolveSecret('GMAIL_WEBHOOK_SECRET');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (secret) headers['X-Webhook-Secret'] = secret;

  const res = await fetch(gmailWebhookPostUrl(inboxId), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Test POST failed: HTTP ${res.status}`);
  const data = (await res.json()) as { event: GmailEvent };
  return data.event;
}

export function defaultLinkedInJobFixture(): GmailEvent {
  return {
    from: 'jobs-noreply@linkedin.com',
    subject: 'New jobs matching Software Engineer',
    body: 'Acme Corp is hiring a Senior Engineer (Remote).',
    roles: [
      { company: 'Acme Corp', title: 'Senior Engineer', url: 'https://linkedin.com/jobs/view/123' },
    ],
  };
}
